<template>
  <v-dialog
    :value="value"
    max-width="980"
    persistent
    content-class="subscription-plan-details-dialog"
    @input="$emit('input', $event)"
  >
    <v-card class="details-modal-card d-flex flex-column">
      <!-- Sticky Header -->
      <div class="details-header">
        <div class="d-flex justify-space-between align-center">
          <div class="text-h5 font-weight-medium">
            {{ $vuetify.lang.t("$vuetify.subscription_plans.plan_details") }}
          </div>
        </div>
      </div>

      <!-- Scrollable Body -->
      <div class="details-body">

        <v-card-text class="pa-6">
          <v-card outlined class="details-body-card pa-4">
            <v-row>
              
              <v-col cols="12">
                <v-card outlined class="info-box pa-4">
                  <div class="info-label">
                    {{ $vuetify.lang.t("$vuetify.subscription_plans.title") }}:
                  </div>

                  <div class="info-value">
                    {{ plan?.title || "N/A" }}
                  </div>
                </v-card>
              </v-col>

              <v-col
                v-for="item in planInfoItems"
                :key="item.label"
                cols="12"
                md="6"
              >
                <v-card outlined class="info-box pa-4">
                  <div class="info-label">
                    {{ item.label }}:
                  </div>

                  <div class="info-value" :class="item.className">
                    {{ item.value }}
                  </div>
                </v-card>
              </v-col>

              <v-col cols="12">
                <v-card outlined class="info-box pa-4">
                  <div class="info-label">
                    {{ $vuetify.lang.t("$vuetify.subscription_plans.description_en") }}:
                  </div>

                  <div class="info-value">
                    {{ plan?.description_en || "N/A" }}
                  </div>
                </v-card>
              </v-col>

              <v-col cols="12">
                <v-card outlined class="info-box pa-4">
                  <div class="info-label">
                    {{ $vuetify.lang.t("$vuetify.subscription_plans.description_jp") }}:
                  </div>

                  <div class="info-value">
                    {{ plan?.description_jp || "N/A" }}
                  </div>
                </v-card>
              </v-col>
            </v-row>
          </v-card>


          <v-card outlined class="mt-5 pa-4">
            <div class="d-flex justify-space-between align-center">
              <div class="text-subtitle-1 font-weight-medium">
                {{ $vuetify.lang.t("$vuetify.subscription_plans.included_features") }}
              </div>

              <div class="primary--text font-weight-medium">
                {{ $vuetify.lang.t("$vuetify.subscription_plans.total") }}:
                {{ sortedFeatures.length }}
              </div>
            </div>
            <v-card outlined class="mt-4 rounded-lg">
              <v-card-text class="pa-0">
                <v-data-table
                  :headers="featureHeaders"
                  :items="sortedFeatures"
                  hide-default-footer
                  disable-pagination
                  dense
                  class="features-table"
                >
                  <template v-slot:[`item.key`]="{ item }">
                    {{ item.key || "N/A" }}
                  </template>

                    <template v-slot:[`item.description_en`]="{ item }">
                      {{ item.description_en || "N/A" }}
                    </template>

                    <template v-slot:[`item.description_jp`]="{ item }">
                      {{ item.description_jp || "N/A" }}
                    </template>

                  <template v-slot:[`item.sequence`]="{ item }">
                    {{ item.sequence ?? "-" }}
                  </template>

                  <template v-slot:[`item.enabled`]="{ item }">
                    <span
                      :class="item.enabled
                        ? 'success--text font-weight-medium'
                        : 'grey--text text--darken-1 font-weight-medium'"
                    >
                      {{
                        item.enabled
                          ? $vuetify.lang.t("$vuetify.subscription_plans.enabled")
                          : $vuetify.lang.t("$vuetify.subscription_plans.disabled")
                      }}
                    </span>
                  </template>
                </v-data-table>
              </v-card-text>
            </v-card>

          </v-card>
        </v-card-text>
      </div>

      <!-- Sticky Footer -->
      <v-card-actions class="details-footer px-6 py-4">
        <v-spacer />

        <v-btn color="error" outlined @click="closeDialog">
          {{ $vuetify.lang.t("$vuetify.common.cancel_btn") }}
        </v-btn>

        <v-btn color="primary" outlined @click="$emit('edit', plan)">
          <v-icon left small>mdi-pencil-outline</v-icon>
          {{ $vuetify.lang.t("$vuetify.subscription_plans.edit_plan_btn") }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { formatPrice } from "@/helpers/subscriptionPlan";

export default {
  name: "SubscriptionPlanDetailsModal",

  props: {
    value: {
      type: Boolean,
      default: false,
    },
    plan: {
      type: Object,
      default: null,
    },
  },

  computed: {
    sortedFeatures() {
      const features = Array.isArray(this.plan?.features)
        ? [...this.plan.features]
        : [];

      return features.sort((a, b) => (a.sequence ?? 0) - (b.sequence ?? 0));
    },

    featureHeaders() {
      return [
        {
          text: this.$vuetify.lang.t("$vuetify.subscription_plans.feature_key"),
          value: "key",
        },
        {
          text: this.$vuetify.lang.t("$vuetify.subscription_plans.feature_description_en"),
          value: "description_en",
        },
        {
          text: this.$vuetify.lang.t("$vuetify.subscription_plans.feature_description_jp"),
          value: "description_jp",
        },
        {
          text: this.$vuetify.lang.t("$vuetify.subscription_plans.feature_sequence"),
          align: "center",
          value: "sequence",
        },
        {
          text: this.$vuetify.lang.t("$vuetify.subscription_plans.status"),
          value: "enabled",
          sortable: false,
        },
      ];
    },

    planInfoItems() {
      return [
        {
          label: this.$vuetify.lang.t("$vuetify.subscription_plans.label_en"),
          value: this.plan?.label_en || "N/A",
          className: "primary--text",
        },
        {
          label: this.$vuetify.lang.t("$vuetify.subscription_plans.label_jp"),
          value: this.plan?.label_jp || "N/A",
          className: "primary--text",
        },
        {
          label: this.$vuetify.lang.t("$vuetify.subscription_plans.status"),
          value: this.plan?.status ?this.$vuetify.lang.t(
            `$vuetify.subscription_plans.options.status.${this.plan?.status}`
          ) : "N/A",
        },
        {
          label: this.$vuetify.lang.t("$vuetify.subscription_plans.price"),
          value: `${formatPrice(this.plan?.price, this.plan?.currency)} (${this.$vuetify.lang.t("$vuetify.subscription_plans.per_member")})`,
        },
        {
          label: this.$vuetify.lang.t("$vuetify.subscription_plans.billing_cycle"),
          value: this.plan?.interval
            ? this.$vuetify.lang.t(`$vuetify.subscription_plans.options.billing_cycle.${this.plan.interval}`)
            : "N/A",
        },
        {
          label: this.$vuetify.lang.t("$vuetify.subscription_plans.billing_type"),
          value: this.plan?.billingType
            ? this.$vuetify.lang.t(`$vuetify.subscription_plans.options.billing_type.${this.plan.billingType}`)
            : "N/A",
        },
        {
          label: this.$vuetify.lang.t("$vuetify.subscription_plans.billing_scheme"),
          value: this.plan?.billingScheme
            ? this.$vuetify.lang.t(`$vuetify.subscription_plans.options.billing_scheme.${this.plan.billingScheme}`)
            : "N/A",
        },
        {
          label: this.$vuetify.lang.t("$vuetify.subscription_plans.sequence"),
          value: this.plan?.sequence ?? "N/A",
        },
        {
          label: this.$vuetify.lang.t("$vuetify.subscription_plans.product_id"),
          value: this.plan?.product_id || "N/A",
        },
        {
          label: this.$vuetify.lang.t("$vuetify.subscription_plans.price_id"),
          value: this.plan?.priceId || "N/A",
        },
      ];
    },
  },

  methods: {
    closeDialog() {
      this.$emit("input", false);
    },
  },
};
</script>

<style scoped>
.details-modal-card {
  height: 85vh;
  max-height: 85vh;
  border-radius: 10px;
  overflow: hidden;
  background: #ffffff;
}

.details-header {
  flex-shrink: 0;
  padding: 20px 32px 10px;
  margin: 0 0 16px 0;
  background: #ffffff;
  position: relative;
  z-index: 2;
}

/* custom divider with padding */
.details-header::after {
  content: "";
  position: absolute;
  bottom: 0;
  left: 28px;
  right: 32px;
  height: 1px;
  background: #e5e7eb;
}

.details-body {
  flex: 1;
  overflow-y: auto;
  background: #ffffff;
}

.details-body-card {
  border-radius: 10px;
  border-color: #e5e7eb;
}
.details-footer {
  flex-shrink: 0;
  background: #ffffff;
}

.info-box {
  border-radius: 10px;
  border-color: #e5e7eb;
  min-height: 72px;
}

.info-label {
  font-size: 14px;
  color: #4b5563;
  margin-bottom: 4px;
}

.info-value {
  font-size: 16px;
  color: #111827;
  font-weight: 500;
  word-break: break-word;
}



.features-table-card {
  border-radius: 10px;
  border-color: #e5e7eb;
}


.features-table {
  padding: 10px;
  border-radius: 10px;
}

::v-deep .features-table table {
  border-collapse: collapse;
}

::v-deep .features-table th {
  font-weight: 500;
  color: #4b5563 !important;
}

::v-deep .features-table td {
  font-size: 14px;
}
::v-deep .features-table {
  border: none;
}

::v-deep .features-table .v-data-table__wrapper {
  border-radius: 10;
}

::v-deep .features-table tbody tr td {
  border-bottom: none !important;
  padding: 14px 16px !important;
}

::v-deep .features-table thead th {
  padding: 12px 16px !important;
  font-weight: 600;
  color: #6b7280 !important;
}
::v-deep .features-table thead tr th {
  border-bottom: 1px solid #eef2f7 !important;
}

@media (max-width: 960px) {
  ::v-deep .features-table tbody tr {
    border-bottom: 1px solid #e5e7eb;
  }

  /* optional: remove last row border */
  ::v-deep .features-table tbody tr:last-child {
    border-bottom: none;
  }
}
</style>